import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PimoTableEventNames,
  PimoTableEventPayload,
} from "@pimo/pimo-components";
import {
  Currency,
  ExtendedLineOfBusiness,
  InitiativeFilterData,
  LineOfBusiness,
} from "aint-types";
import { IN_COLORS } from "aint-utils";
import React, { PropsWithChildren, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { STRAPI_URL } from "../../app/env";
import { InitiativesStatusTableFilter } from "./initiatives-status-table-filter";

export type StatusReportTableContainerProps = PropsWithChildren<{
  title: string;
  showAddInitiativeButton?: boolean;
  addInitiativePath: string;
  filterData: InitiativeFilterData;
  lineOfBusinessFilterOptions: (LineOfBusiness | ExtendedLineOfBusiness)[];
  showCategoryFilter: boolean;
  showBusinessUnitFilter: boolean;
  fireEvent?: (
    eventName: PimoTableEventNames,
    payload: PimoTableEventPayload
  ) => void;
  currency: Currency;
}>;

export const InitiativesStatusTableContainer = React.forwardRef(
  (
    {
      children,
      title,
      addInitiativePath,
      showAddInitiativeButton = false,
      filterData,
      lineOfBusinessFilterOptions,
      showCategoryFilter,
      showBusinessUnitFilter,
      fireEvent,
      currency,
    }: StatusReportTableContainerProps,
    ref
  ) => {
    const navigate = useNavigate();
    const params = useParams<"oeId">();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = (
      _: React.MouseEvent<HTMLLIElement, MouseEvent> | object,
      option?: "includeDraft" | "excludeDraft"
    ) => {
      if (option === "includeDraft") {
        window.open(
          [STRAPI_URL, "api/export", params.oeId, "includeDraft", currency]
            .filter(Boolean)
            .join("/")
        );
      } else if (option === "excludeDraft") {
        window.open(
          [STRAPI_URL, "api/export", params.oeId, "excludeDraft", currency]
            .filter(Boolean)
            .join("/")
        );
      }
      setAnchorEl(null);
    };
    return (
      <ContentBox
        ref={ref}
        icon={
          <Box
            component="img"
            src="checklist.svg"
            sx={{
              backgroundColor: "white",
              borderRadius: "12px",
              p: "5px",
              height: "36px",
            }}
          />
        }
        title={title}
        statusBar={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              disabled={false}
              sx={{
                border: "1px solid",
                borderCollapse: theme.palette.primary.dark,
                borderRadius: "4px",
                color: theme.palette.primary.main,
              }}
              onClick={(e) => {
                showAddInitiativeButton
                  ? handleClick(e)
                  : window.open(
                      [
                        STRAPI_URL,
                        "api/export",
                        params.oeId,
                        "excludeDraft",
                        currency,
                      ]
                        .filter(Boolean)
                        .join("/")
                    );
              }}
            >
              Download Data
            </Button>
            <Button
              sx={{
                borderRadius: "4px",
                background: IN_COLORS.white,
                color: theme.palette.primary.main,
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                marginLeft: "12px",
                "&:hover": {
                  background: IN_COLORS.white,
                },
                ...(showAddInitiativeButton ? {} : { display: "none" }),
              }}
              variant="outlined"
              type="button"
              onClick={() => navigate(addInitiativePath)}
            >
              Add Initiative
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={(e) => {
                handleClose(e, undefined);
              }}
              slotProps={{
                list: {
                  "aria-labelledby": "basic-button",
                },
              }}
            >
              <MenuItem onClick={(e) => handleClose(e, "includeDraft")}>
                Include Draft Initiatives
              </MenuItem>
              <MenuItem onClick={(e) => handleClose(e, "excludeDraft")}>
                Exclude Draft Initiatives
              </MenuItem>
            </Menu>
            <InitiativesStatusTableFilter
              fireEvent={fireEvent}
              filterData={filterData}
              lineOfBusinessFilterOptions={lineOfBusinessFilterOptions}
              showCategoryFilter={showCategoryFilter}
              showBusinessUnitFilter={showBusinessUnitFilter}
            />
          </Box>
        }
      >
        <Box sx={{ overflowY: "auto", width: "100%" }}>{children}</Box>
      </ContentBox>
    );
  }
);

type ContentBoxProps = PropsWithChildren<{
  title: ReactNode;
  icon?: ReactNode;
  statusBar?: ReactNode;
}>;

const ContentBox = React.forwardRef(
  ({ children, icon, title, statusBar }: ContentBoxProps, ref) => {
    const theme = useTheme();
    return (
      <Box
        ref={ref}
        sx={{
          backgroundColor: theme.palette.primary.light,
          borderRadius: theme.spacing(1.5),
          paddingBottom: theme.spacing(1.5),
          overflow: "hidden",
          color: theme.palette.primary.contrastText,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            borderTopLeftRadius: theme.spacing(1.5),
            borderTopRightRadius: theme.spacing(1.5),
            color: theme.palette.primary.contrastText,
            borderBottom: "1px solid",
            borderBottomColor: theme.palette.secondary.main,
            fontWeight: 500,
            gap: 2,
            p: 2,
          }}
        >
          {icon ? (
            <Box
              sx={{
                backgroundColor: theme.palette.common.white,
                borderRadius: "12px",
                height: "36px",
                width: "36px",
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.primary.contrastText,
              lineHeight: "2rem",
            }}
          >
            {title}
          </Typography>
          {statusBar ? (
            <Box
              sx={{
                marginLeft: "auto",
              }}
            >
              {statusBar}
            </Box>
          ) : null}
        </Box>
        {children}
      </Box>
    );
  }
);
